import {
  ChartDataSource,
  ChartType,
  DateAxisType,
  TaskDurationAggregation,
  TargetComparison,
  ValueAggregationType,
} from 'src/app/types/enums'

export interface GenericChartItem {
  groupField: any

  numItems: number
  value: number

  // optional dynamic values for each custom/dynamic series grouping
  // dx does not supported nested paths when defining the 'valueField' of a series
  // so we have to do this instead of including the values in the below array :(
  [key: string]: any
  // the number of items in each custom series
  seriesNumItems: Record<string, number>

  hideNumItems?: boolean // hide default num items row on point-info. info likely in additionalPointInfoValues
  cumulativeNumItems?: number
  seriesCumulativeNumItems?: Record<string, number>
  additionalPointInfoValues?: ChartValue[]
  jobIds: string[]

  /** Target value for this data point (from targetOverrides or default targetValue) */
  target?: number | null
}

export const KNOWN_CHART_ITEM_FIELDS = [
  'groupField',
  'numItems',
  'value',
  'seriesNumItems',
  'hideNumItems',
  'cumulativeNumItems',
  'seriesCumulativeNumItems',
  'additionalPointInfoValues',
  'jobIds',
]

export interface ChartSeries {
  label: string
  value: string
}

export interface ChartValue {
  label: string
  value: number | string
  isNumber?: boolean
  notBold?: boolean
}

export interface ChartConfig {
  cardTitle: string

  dataSourceId: ChartDataSource

  /** a number field on object to chart on y axis.
        if null or DEFAULT_VALUE_FIELD,
        charts will use the number of matched objects */
  valueField: string
  valueAggregationTypeId: ValueAggregationType
  valueAxisTitle?: string
  /** only applies with value aggregation type 'cumulative', and if not splitting/multiple series */
  startingValue: number

  /** field to group objects by on x axis */
  argumentField: string
  argumentFieldIsDate: boolean
  /** split by this field to create separate series on the chart.
   *  has value of DISABLED_SEPARATE_SERIES if not splitting */
  splitByField: string

  /** only show aggregation and splitting categories that exist in filtered data
   *  i.e. filter by date range and any custom filters before generating list of chart series + creating (non-date) aggregate groups
    *  task-duration charts always use filtered categories */
  showFilteredCategoriesOnly: boolean

  dateAxisTypeId: DateAxisType
  dateRangeTypeId?: number
  dateFilterField?: string
  dateFrom?: Date
  dateTo?: Date
  includeCurrentPartialPeriod: boolean
  /** include records whose selected date filter field is null when using a bounded range */
  includeRecordsWithNoDate?: boolean

  chartTypeId: ChartType
  filters?: string
  chartColour?: string
  targetValue?: number
  /** Per-grouping target value overrides. Key is the groupField value (ISO date string for dates, or string value for categories) */
  targetOverrides?: { [key: string]: number }

  /** used by some datasources */
  taskMasterId?: number
  taskMaster2Id?: number
  taskCode?: string
  task1DateField?: string
  task2DateField?: string
  taskDurationAggregation: TaskDurationAggregation

  /** tracking field dataField for Forecast Tasks datasource - values from job-fields-for-charts */
  trackingField?: string

  /** exclude records where the person-field value matches an inactive user */
  excludeInactiveUsers?: boolean

  /** hides the 'sum/average of' and 'number of items'
      lines in the chart tooltip
      i.e. when GenericChartItem's 'additionalValues' are to be used exclusively */
  hideDefaultValueTooltipLines?: boolean

  /** can be in HTML */
  tooltipTitle?: string
}

export interface ChartField {
  dataField: string
  displayName: string
  dataType: 'date' | 'string' | 'number' | 'boolean'
  isDefault?: boolean
  isPersonField?: boolean
  /** job-sourced fields on non-job datasources and job roles get their own dropdown sections */
  fieldGroup?: 'job' | 'jobRole'
  numberPrefix?: string
  numberSuffix?: string
  /** suggested target comparison for new metrics; users can override this per card */
  defaultTargetComparison?: TargetComparison
}

export interface MetricValue {
  label: string
  value: number
  numItems: number
  jobIds: string[]
}

export interface MetricResult {
  value: number
  numItems: number
  jobIds: string[]
  splitValues?: MetricValue[]
}

